import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import useWebSocketMessage from './useWebSocketMessage.js';
import { LogsApiKeys, useLatestLog } from '../../api/index.js';
import { WebSocketMessageTypes, type Log } from '../../models/index.js';
import { capitalize } from '../../utils/index.js';

type LatestLog = {
  dateTime?: string;
  description?: string;
};

const useLiveLatestLog = (patientId: string) => {
  const [log, setLog] = useState<LatestLog>();
  const queryClient = useQueryClient();
  useLatestLog(patientId, {
    onSuccess: (data) => {
      if (data?.code) {
        setLog(formatLog(data));
      }
    },
  });
  const message = useWebSocketMessage<Log>(WebSocketMessageTypes.LOG_UPDATED);

  useEffect(() => {
    // `LOG_UPDATED` is broadcast to all clients; filter to the patient we care
    // about. Invalidate the cached query instead of trusting the WS payload —
    // the review path creates logs backdated to `event.capturedAt`, so the
    // payload's `createdAt` may be older than the truly-latest log for this
    // patient. `invalidateQueries` triggers any active `useLatestLog` observer
    // to refetch (which runs the `onSuccess` setter above).
    if (message?.data?.patientId === patientId) {
      void queryClient.invalidateQueries([LogsApiKeys.GET_LATEST_LOG, patientId]);
    }
  }, [message, patientId, queryClient]);

  const formatLog = (data: Log): LatestLog => {
    return {
      dateTime: DateTime.fromISO(data.createdAt).toFormat('LLL dd, t'),
      description: capitalize(data.code.description),
    };
  };
  return log;
};

export default useLiveLatestLog;
